import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  InlineLoading,
  Search,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import { api, AuditCategory, AuditRow, AuditStats } from '../lib/api';
import { AUDIT_CATEGORY_LABELS, formatDateTime, formatNumber, relativeTime } from '../lib/format';
import { DEFAULT_PAGE_SIZE, lastPageIndex } from '../lib/pagination';
import { Card, Modal, Stat, TablePagination, useToast } from '../components/ui';

const CATEGORIES: Array<{ value: AuditCategory | ''; label: string }> = [
  { value: '', label: 'All activity' },
  { value: 'auth', label: 'Sign-in' },
  { value: 'data', label: 'Data' },
  { value: 'config', label: 'Configuration' },
  { value: 'user', label: 'Accounts' },
  { value: 'system', label: 'System' },
];

/**
 * Who did what, from where.
 *
 * Covers people (sign-ins, configuration changes), facilities (every report received) and the
 * system itself (translation, delivery, jobs abandoned). Addresses are recorded for people and
 * facilities alike, which is what makes "where did this come from" answerable after the fact.
 */
export function Audit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState<AuditCategory | ''>('');
  const [outcome, setOutcome] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AuditRow | null>(null);
  const latestRequest = useRef(0);
  const toast = useToast();

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      if (category) params.set('category', category);
      if (outcome) params.set('outcome', outcome);
      if (search.trim()) params.set('search', search.trim());

      const [list, summary] = await Promise.all([
        api.get<{ rows: AuditRow[]; total: number }>(`/api/audit?${params.toString()}`),
        api.get<AuditStats>('/api/audit/stats'),
      ]);
      // Typing in the search box fires a request per keystroke; only the newest may land.
      if (request !== latestRequest.current) return;
      setTotal(list.total);
      setStats(summary);
      const lastPage = lastPageIndex(list.total, pageSize);
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setRows(list.rows);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [category, outcome, search, page, pageSize, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <div className="grid grid-stats" style={{ marginBottom: 20 }}>
        <Stat
          label="Events recorded"
          value={formatNumber(stats?.total ?? 0)}
          meta="Since the hub was first started"
          tint="var(--accent)"
        />
        <Stat
          label="Last 24 hours"
          value={formatNumber(stats?.last24h ?? 0)}
          meta="Across every category"
        />
        <Stat
          label="Failures (24h)"
          value={formatNumber(stats?.failures24h ?? 0)}
          meta={stats?.failures24h ? 'Rejected sign-ins or actions' : 'Nothing rejected'}
          tint={stats?.failures24h ? 'var(--danger)' : undefined}
        />
        <Stat
          label="Distinct addresses"
          value={formatNumber(stats?.distinctIps7d ?? 0)}
          meta="Seen in the last 7 days"
        />
      </div>

      <div className="audit-toolbar">
        <div className="audit-toolbar-left">
          <Select
            id="audit-category-filter"
            labelText="Filter by category"
            hideLabel
            size="md"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as AuditCategory | '');
              setPage(0);
            }}
            className="audit-category-select"
          >
            {CATEGORIES.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                text={`${option.label}${
                  option.value && stats?.byCategory?.[option.value]
                    ? ` (${stats.byCategory[option.value]})`
                    : ''
                }`}
              />
            ))}
          </Select>

          <Select
            id="audit-outcome-filter"
            labelText="Filter by outcome"
            hideLabel
            size="md"
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value);
              setPage(0);
            }}
            className="audit-outcome-select"
          >
            <SelectItem value="" text="Any outcome" />
            <SelectItem value="success" text="Succeeded" />
            <SelectItem value="failure" text="Failed" />
          </Select>

          <Search
            id="audit-search"
            labelText="Search summary, actor or target"
            placeholder="Search summary, actor or target"
            size="md"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            onClear={() => {
              setSearch('');
              setPage(0);
            }}
            className="audit-search-input"
          />
        </div>

        <div className="audit-toolbar-right">
          <Button
            kind="ghost"
            size="md"
            renderIcon={Renew}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      <Card bodyless>
        <div className="audit-results-panel">
          {loading && rows.length === 0 ? (
            <div className="audit-results-empty">
              <InlineLoading description="Loading activity log…" />
            </div>
          ) : rows.length === 0 ? (
            <div className="audit-results-empty">
              <div className="audit-empty-title">Nothing matches</div>
              <div className="audit-empty-desc">Try widening the filters.</div>
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <Table size="sm">
                  <TableHead>
                    <TableRow>
                      <TableHeader>When</TableHeader>
                      <TableHeader>Activity</TableHeader>
                      <TableHeader>Who</TableHeader>
                      <TableHeader>From</TableHeader>
                      <TableHeader>What happened</TableHeader>
                      <TableHeader />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="muted" title={formatDateTime(row.occurredAt)}>
                          {relativeTime(row.occurredAt)}
                        </TableCell>
                        <TableCell>
                          <div className="stack">
                            <Tag type={row.outcome === 'failure' ? 'red' : 'gray'}>
                              {AUDIT_CATEGORY_LABELS[row.category] ?? row.category}
                            </Tag>
                            <span className="faint small mono">{row.action}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="stack">
                            <span style={{ fontWeight: 560 }}>{row.actorLabel ?? '—'}</span>
                            <span className="faint small">{row.actorType}</span>
                          </div>
                        </TableCell>
                        <TableCell className="mono">{row.ipAddress ?? '—'}</TableCell>
                        <TableCell>
                          <span className="truncate" style={{ maxWidth: 400 }} title={row.summary}>
                            {row.summary}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            kind="ghost"
                            size="sm"
                            onClick={() => setSelected(row)}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={total}
                onChange={(nextPage, nextPageSize) => {
                  setPage(nextPage);
                  setPageSize(nextPageSize);
                }}
              />
            </>
          )}
        </div>
      </Card>

      {selected ? (
        <Modal
          wide
          title={selected.action}
          onClose={() => setSelected(null)}
          footer={
            <Button kind="secondary" onClick={() => setSelected(null)}>
              Close
            </Button>
          }
        >
          <div className="grid grid-stats">
            <div className="stack">
              <span className="faint small">When</span>
              <span>{formatDateTime(selected.occurredAt)}</span>
            </div>
            <div className="stack">
              <span className="faint small">Outcome</span>
              <span className={`pill pill-${selected.outcome === 'failure' ? 'failed' : 'sent'}`}>
                {selected.outcome}
              </span>
            </div>
            <div className="stack">
              <span className="faint small">Address</span>
              <span className="mono">{selected.ipAddress ?? '—'}</span>
            </div>
            <div className="stack">
              <span className="faint small">Actor</span>
              <span>{selected.actorLabel ?? '—'}</span>
            </div>
          </div>

          <div className="stack">
            <span className="faint small">Summary</span>
            <span>{selected.summary}</span>
          </div>

          {selected.targetLabel ? (
            <div className="stack">
              <span className="faint small">Target</span>
              <span className="mono">
                {selected.targetType}: {selected.targetLabel}
              </span>
            </div>
          ) : null}

          {selected.userAgent ? (
            <div className="stack">
              <span className="faint small">Client</span>
              <span className="small mono">{selected.userAgent}</span>
            </div>
          ) : null}

          {selected.detail ? (
            <div>
              <div className="card-title" style={{ marginBottom: 8 }}>
                Detail
              </div>
              <div className="code-block">{JSON.stringify(selected.detail, null, 2)}</div>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
